import type { IsoDate, TimeRange, TimeRangeOnDate } from "@thalamos/common";
import {
  dayjs,
  ISO_DATE_FORMAT,
  isoWeekdayOf,
  longestOverlap,
  mergeAdjacentTimeRanges,
  mergeTimeRangesOnDate,
  parseTimeOfDay,
  subtractTimeRanges,
  unwrapOrThrow,
  widenToWholeMinutes,
} from "@thalamos/common";
import type {
  RouteTimeResult,
  Section12DoctorSearchResult,
  Section12Gender,
} from "@thalamos/aspire-common";
import {
  ASSESSMENT_DURATION_MINUTES,
  NEAR_AVAILABILITY_MINUTES,
} from "@thalamos/aspire-common";

import { requestFromInstants } from "../../RequestedAvailability/requestedAvailability.js";

export interface DoctorMatchCriteria {
  specialismIds: string[];
  /** The time being asked for. Empty means availability is not being filtered on. */
  requestedRanges: TimeRangeOnDate[];
  genderValues: Section12Gender[];
  languageIds: string[];
}

export interface MatchResult {
  matchesSpecialisms: boolean;
  matchesAvailability: boolean;
  matchesGender: boolean;
  matchesLanguages: boolean;
  matchesTravelDistance: boolean; // Doctor is within their own stated max travel time
  isMatch: boolean; // Every applied criterion matches
  matchedSpecialismCount: number; // Number of specialisms that match the selected criteria
  matchedLanguageCount: number; // Number of languages that match the selected criteria
  /** Free for long enough to be worth asking, but not for a whole assessment. */
  hasNearAvailability: boolean;
  /** The stretch that fell short, so it can be named rather than alluded to. */
  nearAvailabilityStretch?: TimeRangeOnDate;
}

/**
 * How well a doctor's free time fits the time being asked for. A doctor who
 * fits at all is told where: the longest unbroken free stretch inside any one
 * range, which is what the tier was judged on.
 */
type AvailabilityMatch =
  { tier: "none" } | { tier: "available" | "near"; stretch: TimeRangeOnDate };

/**
 * When a doctor is free on one date, as merged ranges, less the time they have
 * already given to an assessment.
 *
 * A date the doctor has overridden is answered by that override alone, so an
 * override with no blocks is an explicit day off. Which dates carry an override
 * is the payload's to say — see
 * {@link Section12DoctorSearchResult.overriddenDates}.
 */
export function doctorAvailabilityOn(
  doctor: Section12DoctorSearchResult,
  date: IsoDate,
): TimeRange[] {
  const weekday = isoWeekdayOf(dayjs.utc(date, ISO_DATE_FORMAT));
  const blocks = doctor.overriddenDates.includes(date)
    ? doctor.availabilityOverrideBlocks.filter((block) => block.date === date)
    : doctor.availabilityBlocks.filter((block) => block.dayOfWeek === weekday);

  return subtractTimeRanges(
    mergeAdjacentTimeRanges(blocks),
    bookedTimeOf(doctor).filter((range) => range.date === date),
  );
}

/**
 * The doctor's booked time as wall-clock ranges on the grid. An assessment
 * agreed off the quarter hour is widened to the cells it touches, so the
 * doctor is never offered a cell they are partly committed for.
 *
 * A range that fails to convert has been through the schema and then failed
 * a conversion it should survive: a broken guarantee, so it is thrown rather
 * than dropped. Silently losing booked time would offer the doctor a cell
 * they are committed for.
 */
function bookedTimeOf(doctor: Section12DoctorSearchResult): TimeRangeOnDate[] {
  return mergeTimeRangesOnDate(
    doctor.bookedTimeRanges.flatMap(
      (range) =>
        unwrapOrThrow(
          widenToWholeMinutes(range).andThen((wholeMinutes) =>
            requestFromInstants([wholeMinutes]),
          ),
        ).ranges,
    ),
  );
}

function tierOf(minutes: number): AvailabilityMatch["tier"] {
  if (minutes >= ASSESSMENT_DURATION_MINUTES) {
    return "available";
  }
  if (minutes >= NEAR_AVAILABILITY_MINUTES) {
    return "near";
  }
  return "none";
}

/**
 * How the doctor's free time fits the request: the longest single stretch they
 * are free for inside any one range, measured against a whole assessment.
 *
 * Only what falls inside a range counts, and only in one piece — an assessment
 * cannot be split across a gap, nor run past the time it was asked for.
 */
function matchAvailability(
  doctor: Section12DoctorSearchResult,
  ranges: readonly TimeRangeOnDate[],
): AvailabilityMatch {
  let best: { minutes: number; stretch: TimeRangeOnDate } | undefined;

  for (const range of mergeTimeRangesOnDate(ranges)) {
    const stretch = longestOverlap(
      doctorAvailabilityOn(doctor, range.date),
      range,
    );
    if (stretch === undefined) {
      continue;
    }
    const minutes =
      parseTimeOfDay(stretch.until) - parseTimeOfDay(stretch.from);
    if (best === undefined || minutes > best.minutes) {
      best = { minutes, stretch: { date: range.date, ...stretch } };
    }
  }

  if (best === undefined) {
    return { tier: "none" };
  }
  const tier = tierOf(best.minutes);
  return tier === "none" ? { tier } : { tier, stretch: best.stretch };
}

/**
 * Whether the drive is within the doctor's own maximum travel time. A doctor
 * with no maximum, or with no known drive, is treated as within it.
 */
export function isWithinTravelLimit(
  doctor: Section12DoctorSearchResult,
  routeTime: RouteTimeResult | undefined,
): boolean {
  if (
    doctor.maxTravelTimeMinutes == null ||
    routeTime?.drivingTimeMinutes == null
  ) {
    return true;
  }
  return routeTime.drivingTimeMinutes <= doctor.maxTravelTimeMinutes;
}

/**
 * Determine if a doctor matches the filter criteria.
 *
 * Matching logic:
 * - Specialism filter: OR logic (doctor has ANY of the selected specialisms)
 * - Gender filter: OR logic (doctor has ANY of the selected genders)
 * - Language filter: OR logic (doctor speaks ANY of the selected languages)
 * - Availability filter: the doctor is free for a whole assessment inside at
 *   least one of the ranges being asked for
 * - Overall match: AND logic (must meet ALL criteria that are applied)
 *
 * If no filters are applied, all doctors are considered matching.
 */
export function matchDoctor(
  doctor: Section12DoctorSearchResult,
  criteria: DoctorMatchCriteria,
  routeTime?: RouteTimeResult,
): MatchResult {
  const hasSpecialismFilter = criteria.specialismIds.length > 0;
  const hasAvailabilityFilter = criteria.requestedRanges.length > 0;
  const hasGenderFilter = criteria.genderValues.length > 0;
  const hasLanguageFilter = criteria.languageIds.length > 0;

  // Count matched specialisms
  const matchedSpecialismCount = hasSpecialismFilter
    ? doctor.specialisms.filter((spec) =>
        criteria.specialismIds.includes(spec.id),
      ).length
    : 0;

  // Count matched languages
  const matchedLanguageCount = hasLanguageFilter
    ? doctor.languages.filter((language) =>
        criteria.languageIds.includes(language.id),
      ).length
    : 0;

  // Check specialism match (OR logic - doctor has ANY of selected specialisms)
  const matchesSpecialisms = !hasSpecialismFilter || matchedSpecialismCount > 0;

  // Check gender match (OR logic - doctor has ANY of the selected genders)
  const matchesGender =
    !hasGenderFilter ||
    (doctor.gender != null && criteria.genderValues.includes(doctor.gender));

  // Check language match (OR logic - doctor speaks ANY of the selected languages)
  const matchesLanguages = !hasLanguageFilter || matchedLanguageCount > 0;

  const availability = hasAvailabilityFilter
    ? matchAvailability(doctor, criteria.requestedRanges)
    : undefined;

  const matchesAvailability =
    availability === undefined || availability.tier === "available";

  const hasNearAvailability =
    !matchesAvailability && availability?.tier === "near";

  const matchesTravelDistance = isWithinTravelLimit(doctor, routeTime);

  // Overall match requires meeting ALL criteria (AND logic)
  const isMatch =
    matchesSpecialisms &&
    matchesGender &&
    matchesLanguages &&
    matchesAvailability &&
    matchesTravelDistance;

  return {
    matchesSpecialisms,
    matchesAvailability,
    matchesGender,
    matchesLanguages,
    matchesTravelDistance,
    isMatch,
    matchedSpecialismCount,
    matchedLanguageCount,
    hasNearAvailability,
    nearAvailabilityStretch:
      availability?.tier === "near" ? availability.stretch : undefined,
  };
}

export interface DoctorWithMatchInfo {
  doctor: Section12DoctorSearchResult;
  matchedSpecialismCount: number;
  matchedLanguageCount: number;
  hasNearAvailability: boolean;
  nearAvailabilityStretch?: TimeRangeOnDate;
  matchesGender: boolean;
  matchesLanguages: boolean;
  matchesTravelDistance: boolean;
}

/**
 * Split doctors into matching and non-matching groups based on filter criteria.
 * Used to display two sections in the doctor list.
 * Returns additional match metadata for non-matching doctors.
 */
export function splitDoctorsByMatch(
  doctors: Section12DoctorSearchResult[],
  criteria: DoctorMatchCriteria,
  routeTimes?: Map<string, RouteTimeResult>,
): {
  matching: Section12DoctorSearchResult[];
  nonMatching: DoctorWithMatchInfo[];
} {
  const matching: Section12DoctorSearchResult[] = [];
  const nonMatching: DoctorWithMatchInfo[] = [];

  doctors.forEach((doctor) => {
    const routeTime = routeTimes?.get(doctor.id);
    const result = matchDoctor(doctor, criteria, routeTime);
    if (result.isMatch) {
      matching.push(doctor);
    } else {
      nonMatching.push({
        doctor,
        matchedSpecialismCount: result.matchedSpecialismCount,
        matchedLanguageCount: result.matchedLanguageCount,
        hasNearAvailability: result.hasNearAvailability,
        nearAvailabilityStretch: result.nearAvailabilityStretch,
        matchesGender: result.matchesGender,
        matchesLanguages: result.matchesLanguages,
        matchesTravelDistance: result.matchesTravelDistance,
      });
    }
  });

  // Sort non-matching doctors: near availability first, then by specialism match count (descending)
  nonMatching.sort((a, b) => {
    // Near availability takes priority
    if (a.hasNearAvailability && !b.hasNearAvailability) return -1;
    if (!a.hasNearAvailability && b.hasNearAvailability) return 1;

    // Then sort by matched specialism count (descending)
    return b.matchedSpecialismCount - a.matchedSpecialismCount;
  });

  return { matching, nonMatching };
}
